import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { Upload, Select, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Button } from "src/components/ui/Button";
import DocumentImg from "src/assets/images/DocumentImg.png";
import { LeftArrowWhite } from "src/assets/images/svg/LeftArrow";
import { UploadSvg } from "src/assets/images/svg/Upload";
import UploadProcessingWithAnimationComponent from "src/components/UploadProcessingWithAnimationComponent";
import { applicationStore } from "src/store/ApplicationStore";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import { NodeTypes } from "src/enums/NodeTypes";
import {
  MAX_FILE_SIZE_MB_FP
} from "src/constants/fileConfig";
import {
  CompanyDocTypes,
  getCompanyDocTypeLabel,
} from "src/enums/CompanyDocTypes";
import { ClientDocTypes } from "src/enums/ClientDocTypes";

const { Dragger } = Upload;

const ACCEPTED_MIME_TYPES = ["application/pdf"];

const getAcceptAttribute = (mimeTypes) =>
  mimeTypes
    .map((type) => {
      if (type === "application/pdf") {
        return ".pdf";
      }
      if (type.startsWith("image/")) {
        return `.${type.split("/")[1]}`;
      }
      return type;
    })
    .join(",");

const convertFileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });

const FileUploadPage = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedDocType, setSelectedDocType] = useState(null);
  const [filePayload, setFilePayload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const workflowNextServiceResponse =
    applicationStore.workflowNextServiceResponse;

  const workflowResponse = useMemo(() => {
    return workflowNextServiceResponse || {};
  }, [workflowNextServiceResponse]);

  const workflowIdType = workflowResponse.id_type;

  const isCompanyOrClient = useMemo(() => {
    return workflowIdType !== "client";
  }, [workflowIdType]);

  const nodeSettingsForPlayer = workflowResponse.node_settings_for_player || {};
  const isUploadMandatory = nodeSettingsForPlayer?.mandatory !== false;

  const title = nodeSettingsForPlayer?.label || selectedDocType?.split('_')?.join(' ')?.toLocaleLowerCase()
  nodeSettingsForPlayer?.label ||
    workflowResponse.description ||
    t("fileUpload.titleFallback");

  const description =
    nodeSettingsForPlayer?.instruction ||
    workflowResponse.instruction ||
    t("fileUpload.descriptionFallback");

  const availableDocTypes = useMemo(() => {
    if (!Array.isArray(nodeSettingsForPlayer?.document_type)) return [];
    return nodeSettingsForPlayer.document_type
      .filter(Boolean)
      .map((type) => ({
        value: type,
        label: getCompanyDocTypeLabel(type),
      }));
  }, [nodeSettingsForPlayer?.document_type]);


  useEffect(() => {
    if (availableDocTypes.length > 0) {
      setSelectedDocType((previous) => previous || availableDocTypes[0].value);
    } else {
      setSelectedDocType(null);
    }
  }, [availableDocTypes]);

  const handleRemoveFile = useCallback(() => {
    setFilePayload(null);
    setFileList([]);
  }, []);

  const handleBeforeUpload = useCallback(
    async (file) => {
      const sizeInMb = file.size / (1024 * 1024);
      if (sizeInMb > MAX_FILE_SIZE_MB_FP) {
        toast.warning(t("fileUpload.fileTooLarge", { size: MAX_FILE_SIZE_MB_FP }));
        return Upload.LIST_IGNORE;
      }

      const acceptedMimeTypes =
        Array.isArray(nodeSettingsForPlayer?.accepted_mime_types) &&
          nodeSettingsForPlayer.accepted_mime_types.length > 0
          ? nodeSettingsForPlayer.accepted_mime_types
          : ACCEPTED_MIME_TYPES;
      if (!acceptedMimeTypes.includes(file.type)) {
        toast.error(
          t("fileUpload.invalidType", { types: acceptedMimeTypes.join(", ") })
        );
        return Upload.LIST_IGNORE;
      }

      try {
        const base64 = await convertFileToBase64(file);
        setFilePayload({
          name: file.name,
          content: base64,
          type: file.type,
        });
        setFileList([{ uid: file.uid, name: file.name, status: "done" }]);
      } catch (error) {
        toast.error(t("fileUpload.processError"));
        return Upload.LIST_IGNORE;
      }

      return false;
    },
    [nodeSettingsForPlayer?.accepted_mime_types, t]
  );

  const fileType = useMemo(() => {

    if (nodeSettingsForPlayer?.document_type?.length === 0) {
      return nodeSettingsForPlayer.document_type[0];
    }

    if (workflowIdType === "company" && availableDocTypes.length === 0) {
      return CompanyDocTypes.OTHER;
    } else if (workflowIdType === "client") {
      return ClientDocTypes.OTHER;
    }

    if (selectedDocType) {
      return selectedDocType;
    }

    return workflowIdType === "company"
      ? CompanyDocTypes.OTHER
      : ClientDocTypes.OTHER;
  }, [nodeSettingsForPlayer.document_type, workflowIdType, availableDocTypes.length, selectedDocType]);

  const handleSubmit = useCallback(async () => {
    const hasFile = Boolean(filePayload);

    if (!hasFile && isUploadMandatory) {
      toast.error(t("fileUpload.fileRequired"));
      return;
    }

    const requireDocType =
      availableDocTypes.length > 0 && (hasFile || isUploadMandatory);
    if (requireDocType && !selectedDocType) {
      toast.error(t("fileUpload.documentTypeRequired"));
      return;
    }
    if (!isUploadMandatory && !hasFile) {
      try {
        setUploading(true);
        const response = await workflowStepIterator({
          access_token: applicationStore.access_token,
          body: {},
          constextOfStep: {
            step: isCompanyOrClient ? NodeTypes.FILE_UPLOAD : NodeTypes.FILE_UPLOAD_KYC,
            retry: workflowResponse.retry,
            step_skip: true,
            step_sub_type: fileType,
            node_id: workflowResponse.node_id,
          },
        });
        if (response?.status !== 200) {
          throw new Error("File upload skip step failed");
        }

        applicationStore.setWorkflowNextServiceResponse(response.data);
        navigate("/next", { replace: true });
      } catch (error) {
        if (error?.response?.status === 406) {
          toast.error(error?.response?.data?.detail || t("errors.generic"));
        } else {
          applicationStore.setApplicationErrorObj({
            header: t("fileUpload.stepFailedHeader"),
            description: t("fileUpload.stepFailedDescription"),
            buttonList: [
              {
                label: t("actions.retry"),
                link: "/next",
                variant: "primaryWithoutIcon",
              },
            ],
          });
          navigate("/error-page", { replace: true });
        }
      } finally {
        setUploading(false);
      }
      return;
    }

    const payload = isCompanyOrClient
      ? {
        file_type: fileType,
        file_name: filePayload.name,
        file_content: filePayload.content,
        file_label: title,
      }
      : {
        id_frontimage: filePayload.content,
        id_backimage: "",
        docs_type: ClientDocTypes.OTHER,
        file_label: title,
        notes: "",
        country: workflowResponse.country || "GB",
      };

    try {
      setUploading(true);
      const response = await workflowStepIterator({
        access_token: applicationStore.access_token,
        node_id: workflowResponse.node_id,
        body: payload,
        action_index: workflowResponse.index,
        constextOfStep: {
          step: isCompanyOrClient ? NodeTypes.FILE_UPLOAD : NodeTypes.FILE_UPLOAD_KYC,
          retry: workflowResponse.retry,
        },
      });

      if (response.data?.service_results?.[0]?.status !== 200) {
        throw new Error("File upload step failed");
      }

      applicationStore.setWorkflowNextServiceResponse(response.data);
      navigate("/next", { replace: true });
    } catch (error) {
      if (error?.response?.status === 406) {
        toast.error(error?.response?.data?.detail || t("errors.generic"));
      } else {
        applicationStore.setApplicationErrorObj({
          header: t("fileUpload.stepFailedHeader"),
          description: t("fileUpload.stepFailedDescription"),
          buttonList: [
            {
              label: t("actions.retry"),
              link: "/next",
              variant: "primaryWithoutIcon",
            },
          ],
        });
        navigate("/error-page", { replace: true });
      }
    } finally {
      setUploading(false);
    }
  }, [filePayload, isUploadMandatory, availableDocTypes.length, selectedDocType, fileType, title, t, isCompanyOrClient, navigate, workflowResponse]);

  if (uploading) {
    return (
      <UploadProcessingWithAnimationComponent
        title={t("fileUpload.processingTitle")}
        message={t("fileUpload.processingMessage")}
      />
    );
  }

  const acceptedMimeTypes =
    Array.isArray(nodeSettingsForPlayer?.accepted_mime_types) &&
      nodeSettingsForPlayer.accepted_mime_types.length > 0
      ? nodeSettingsForPlayer.accepted_mime_types
      : ACCEPTED_MIME_TYPES;
  const acceptAttribute = getAcceptAttribute(acceptedMimeTypes);
  const shouldRequireDocTypeSelection =
    availableDocTypes.length > 0 && (Boolean(filePayload) || isUploadMandatory);
  const isSubmitDisabled =
    (isUploadMandatory && !filePayload) ||
    (shouldRequireDocTypeSelection && !selectedDocType);
  const isUploadDisabled = Boolean(filePayload);

  return (
    <div className="custom-container">
      <div className="flex flex-col h-full justify-between">
        <div>
          <div className="text-left">
            <Button
              variant="primaryBack"
              onClick={() =>
                navigate(`/?aid=${applicationStore.aidCode}`, {
                  replace: true,
                })
              }
            >
              <LeftArrowWhite />
            </Button>
          </div>

          <div className="grid gap-4 mt-10">
            <h1 className="text-h5 font-bold text-flow-heading capitalize">{title}</h1>
            <p className="text-body text-Gray900">{description}</p>
          </div>

          {availableDocTypes.length > 1 && (
            <div className="mt-8">
              <Select
                className="w-full notranslate"
                placeholder={t("fileUpload.selectPlaceholder")}
                value={selectedDocType}
                onChange={setSelectedDocType}
                options={availableDocTypes}
                size="large"
              />
            </div>
          )}

          <div className="mt-6">
            <Dragger
              name="document"
              multiple={false}
              beforeUpload={handleBeforeUpload}
              fileList={fileList}
              showUploadList={false}
              disabled={isUploadDisabled}
              accept={acceptAttribute}
              className={`flow-upload-dragger !rounded-[24px] !py-8 ${isUploadDisabled ? "cursor-not-allowed" : ""}`}
              style={isUploadDisabled ? { borderStyle: "none" } : undefined}
            >
              <div className="flex flex-col items-center gap-4 ">
                <UploadSvg />
                <div className="text-center">

                  <p className="text-flow-heading text-xs">
                    {t("fileUpload.dragSubtitle")}{" "}
                    <span className="flow-upload-browse">
                      {t("fileUpload.browse")}
                    </span>
                  </p>
                  <p className="text-xs text-Gray700 mt-2">
                    {t("fileUpload.fileLimit", { size: MAX_FILE_SIZE_MB_FP })}
                    <br />
                    {t("fileUpload.supportedTypes", {
                      types: acceptedMimeTypes
                        .map((type) => type.split("/").pop()?.toUpperCase())
                        .join(", "),
                    })}
                  </p>
                </div>
              </div>
            </Dragger>
          </div>

          {filePayload && (
            <div className="flow-upload-file-card mt-4 flex items-center justify-between rounded-lg border border-SorrellBrown200 gap-2 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <img src={DocumentImg} alt="Document" width={16} height={16} />
                <Tooltip title={filePayload.name} placement="top">
                  <div className="text-xs text-Gray900 truncate max-w-40">
                    {filePayload.name}
                  </div>
                </Tooltip>
              </div>
              <Button
                type="link"
                htmlType="button"
                size="small"
                onClick={handleRemoveFile}
                className="text-xs flow-upload-inline-action px-0"
              >
                {t("fileUpload.remove")}
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-2 pb-16">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {t("actions.continue")}
          </Button>

          {window?.top === window?.self && (
            <Button
              variant="secondaryWithoutIcon"
              onClick={() => {
                applicationStore.setContinueOnAnotherDevice("/file-upload");
                navigate("/continue-on-another-device", { replace: true });
              }}
            >
              {t("fileUpload.continueOnAnotherDevice")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

export default FileUploadPage;
